import { ProductRecommendation } from '@/types'
import { Heading } from '@big-red-group/storefront-common-ui'
import { Image, Name, Product, RatingContainer, Wrapper, FromPrice, PointsBadgeContainer } from './Recommended.styles'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { ProductListCarousel } from './Swiper.styles'
import { useProductClicked } from '@/hooks/AnalyticsProductHook'
import { useProductListImpression } from '@/hooks/AnalyticsImpressionHook'
import { useRouter } from 'next/router'
import ProductPointsBadge from './ProductPointsBadge'
import { getPoints } from '@/helpers/LoyaltyCampaignHelper'
import { useContext } from 'react'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { getPointsBadgeIcon, getPointsDescription, getPointsBadgeStyles } from '@/helpers/LoyaltyCampaignHelper'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { formatCurrency } from '@big-red-group/storefront-common-checkout'
import { RichSnippetContext } from '@/context/RichSnippetContext'

const Recommended: React.FC<{
  title: string
  products: ProductRecommendation[]
  dataTestId?: string
}> = ({ title, products, dataTestId }) => {
  const trackProductClicked = useProductClicked()
  // TODO: Figure out whether list ID is available
  const impressionRef = useProductListImpression({ products, listName: title })
  const router = useRouter()
  const { lang } = router.query
  const defaultProperties = useContext(DefaultPropertiesContext)
  const loyaltyCampaign = defaultProperties?.loyaltyCampaign
  const handlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()
  collect({
    type: 'Carousel',
    data: products.map((p) => ({
      url: `https://${baseUrl}/${lang}/${p.regionUrlSegment}/${p.productUrlSegment}`,
      name: p.productName,
      image: p.images[0],
    })),
  })

  return (
    <>
      <Wrapper ref={impressionRef}>
        <Heading size="L" dataTestId={dataTestId}>
          {title}
        </Heading>
        <ProductListCarousel>
          <Swiper
            breakpoints={{
              0: {
                spaceBetween: 16,
                slidesPerView: 'auto',
              },
              768: {
                spaceBetween: 16,
                slidesPerView: 3,
                loop: false,
              },
            }}
          >
            {products?.map(
              ({ images, productName, productId, rating, productUrlSegment, regionUrlSegment, fromPrice }, index) => (
                <SwiperSlide key={index}>
                  <Product
                    data-testid={`recommended-product-${productId}`}
                    href={`${baseUrl}/${lang}/${regionUrlSegment}/${productUrlSegment}`}
                    key={productId}
                    // TODO: Figure out whether list ID is available (or can we just hash the list title to form a list ID?)
                    onClick={() =>
                      trackProductClicked({
                        product: products[index],
                        position: index + 1,
                        isRecommended: true,
                        listTitle: title,
                      })
                    }
                  >
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <Image style={{ backgroundImage: `url(${images && images[0]})` }} />
                    {rating && (
                      <RatingContainer>
                        <svg
                          clipRule="evenodd"
                          fillRule="evenodd"
                          strokeLinejoin="round"
                          strokeMiterlimit="2"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                        >
                          <path
                            d="m11.322 2.923c.126-.259.39-.423.678-.423.289 0 .552.164.678.423.974 1.998 2.65 5.44 2.65 5.44s3.811.524 6.022.829c.403.055.65.396.65.747 0 .19-.072.383-.231.536-1.61 1.538-4.382 4.191-4.382 4.191s.677 3.767 1.069 5.952c.083.462-.275.882-.742.882-.122 0-.244-.029-.355-.089-1.968-1.048-5.359-2.851-5.359-2.851s-3.391 1.803-5.359 2.851c-.111.06-.234.089-.356.089-.465 0-.825-.421-.741-.882.393-2.185 1.07-5.952 1.07-5.952s-2.773-2.653-4.382-4.191c-.16-.153-.232-.346-.232-.535 0-.352.249-.694.651-.748 2.211-.305 6.021-.829 6.021-.829s1.677-3.442 2.65-5.44z"
                            fillRule="nonzero"
                          />
                        </svg>
                        {rating}
                      </RatingContainer>
                    )}
                    <Name>{productName}</Name>
                    {/* TODO review */}
                    {/* {tour && (
                <Tour>
                  {tour.time} • {tour.region}
                </Tour>
              )} */}
                    {fromPrice && <FromPrice>From {formatCurrency(fromPrice)}</FromPrice>}
                    {fromPrice && loyaltyCampaign && (
                      <PointsBadgeContainer style={{ marginTop: '10px' }}>
                        <ProductPointsBadge
                          icon={getPointsBadgeIcon(handlerKey)}
                          points={getPoints(fromPrice!, loyaltyCampaign)!}
                          description={getPointsDescription(handlerKey)}
                          style={getPointsBadgeStyles(handlerKey)}
                        />
                      </PointsBadgeContainer>
                    )}
                  </Product>
                </SwiperSlide>
              )
            )}
          </Swiper>
        </ProductListCarousel>
      </Wrapper>
    </>
  )
}

export default Recommended
